import { sanitiseForLLM, SANITISE_SELECTOR_LIMIT } from "../sanitise/forLLM.js";

const RAGE_CLICK_WINDOW_MS = 1000;
const RAGE_CLICK_THRESHOLD = 3;
const MAX_TRACKED_TARGETS = 64;

function describeTarget(target: EventTarget | null): string {
  if (!(target instanceof Element)) return 'unknown';
  const tag = target.tagName.toLowerCase();
  if (target.id) return `${tag}#${target.id}`.slice(0, 64);
  const testId = target.getAttribute('data-testid');
  if (testId) return `${tag}[data-testid=${testId}]`.slice(0, 64);
  const cls = target.classList[0];
  if (cls) return `${tag}.${cls}`.slice(0, 64);
  return tag;
}

interface TargetState {
  /** Wall-clock timestamps of clicks within the current window. */
  clicks: number[];
  /**
   * Debounce timer handle. Set when a cluster forms (≥ threshold clicks),
   * reset on each additional click in the same cluster. When it fires the
   * cluster is over and we emit once with the final count.
   */
  timer: ReturnType<typeof setTimeout> | null;
}

type Push = (name: string, properties?: Record<string, unknown>) => void;

export function startRageClickTracking(push: Push): () => void {
  if (typeof window === 'undefined') return () => {};

  const targetMap = new Map<string, TargetState>();

  function handler(e: MouseEvent): void {
    const selector = sanitiseForLLM(describeTarget(e.target), SANITISE_SELECTOR_LIMIT);
    const now = Date.now();

    if (!targetMap.has(selector)) {
      if (targetMap.size >= MAX_TRACKED_TARGETS) {
        const [firstKey] = targetMap.keys();
        if (firstKey !== undefined) targetMap.delete(firstKey);
      }
      targetMap.set(selector, { clicks: [], timer: null });
    }

    const state = targetMap.get(selector)!;

    // Prune clicks that have fallen outside the window.
    const windowStart = now - RAGE_CLICK_WINDOW_MS;
    let i = 0;
    while (i < state.clicks.length && (state.clicks[i] ?? 0) < windowStart) i++;
    if (i > 0) state.clicks.splice(0, i);

    state.clicks.push(now);

    if (state.clicks.length < RAGE_CLICK_THRESHOLD) return;

    // Cluster is active. Reset the debounce timer so we emit once the cluster
    // ends — meaning no further click on this target for a full window period.
    // The count at that point reflects every click in the cluster.
    if (state.timer !== null) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      const count = state.clicks.length;
      state.clicks = [];
      state.timer = null;
      push('rage_click', { target: selector, count });
    }, RAGE_CLICK_WINDOW_MS);
  }

  window.addEventListener('click', handler, { capture: true });

  return () => {
    window.removeEventListener('click', handler, true);
    for (const state of targetMap.values()) {
      if (state.timer !== null) clearTimeout(state.timer);
    }
    targetMap.clear();
  };
}
